/**
 * Edit Request View
 *
 * Page view for editing an existing draft expenditure request.
 * Features:
 * - Loads existing draft data into form
 * - Allows editing all fields
 * - Manages attachments (add/remove)
 * - Shows last saved timestamp
 * - Handles concurrent edit conflicts via version checking
 */

import { createRequestForm, type RequestFormData, RequestForm } from '../components/request-form';
import { createFileUpload, FileUpload, type UploadedFile } from '../components/file-upload';
import { renderIcon } from '../utils/icons';
import { apiClient } from '../services/api-client';
import { alert, confirm } from '../components/modal';
import { createSidebarLayout } from '../components/sidebar-layout';
import type { RouteContext } from '../services/router';

/**
 * API response for a single expenditure request
 */
interface ExpenditureRequestResponse {
  id: string;
  title: string | null;
  description: string | null;
  amount: number | null;
  currency: string | null;
  category: string | null;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  version: number;
}

/**
 * API response for attachments
 */
interface AttachmentResponse {
  id: string;
  request_id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  size: number;
  created_at: string;
}

/**
 * Create the edit request view
 */
export function editRequestView(context: RouteContext): HTMLElement {
  const requestId = context.params.id;
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6 max-w-3xl mx-auto';

  // Loading state
  const loadingContainer = document.createElement('div');
  loadingContainer.id = 'edit-request-loading';
  loadingContainer.className = 'glass-card p-8 rounded-2xl text-center';
  loadingContainer.innerHTML = `
    <div class="flex justify-center mb-4">
      ${renderIcon('loader', { size: 'xl', className: 'text-primary animate-spin' }).outerHTML}
    </div>
    <h2 class="text-xl font-bold text-foreground mb-2">Loading Request</h2>
    <p class="text-muted-foreground">Please wait while we load your draft...</p>
  `;

  // Form container (hidden initially)
  const formContainer = document.createElement('div');
  formContainer.id = 'edit-request-form-container';
  formContainer.className = 'hidden';

  // Last saved timestamp
  const lastSavedContainer = document.createElement('div');
  lastSavedContainer.id = 'last-saved-container';
  lastSavedContainer.className = 'flex items-center gap-2 text-sm text-muted-foreground mb-4 hidden';
  lastSavedContainer.innerHTML = `
    ${renderIcon('clock', { size: 'sm' }).outerHTML}
    <span id="last-saved-text">Last saved: Never</span>
  `;

  // File attachments section
  const attachmentsSection = document.createElement('div');
  attachmentsSection.className = 'glass-card p-6 rounded-2xl mt-6';
  attachmentsSection.innerHTML = `
    <div class="flex items-center gap-3 mb-4">
      ${renderIcon('paperclip', { size: 'md', className: 'text-primary' }).outerHTML}
      <h2 class="text-lg font-semibold text-foreground">Attachments</h2>
    </div>
    <div id="file-upload-container"></div>
  `;

  content.appendChild(lastSavedContainer);
  content.appendChild(loadingContainer);
  content.appendChild(formContainer);
  content.appendChild(attachmentsSection);

  // Wrap with sidebar layout
  const container = createSidebarLayout(content, {
    currentPath: `/requests/${requestId}/edit`,
    title: 'Edit Request',
    subtitle: 'Update your draft expenditure request',
  });

  // State
  let currentRequest: ExpenditureRequestResponse | null = null;
  let currentVersion: number = 0;
  let formInstance: RequestForm | null = null;
  let fileUploadInstance: FileUpload | null = null;
  let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

  // Load the request data
  setTimeout(async () => {
    try {
      // Load request data
      const response = await apiClient.get<{ data: ExpenditureRequestResponse }>(`/requests/${requestId}`);
      currentRequest = response.data.data;
      currentVersion = currentRequest.version;

      // Check if the request is a draft
      if (currentRequest.status !== 'DRAFT') {
        await alert({
          title: 'Cannot Edit',
          message: 'Only draft requests can be edited. This request has already been submitted.',
          buttonText: 'Go to Dashboard',
          type: 'error',
        });
        window.location.href = '/dashboard';
        return;
      }

      // Load attachments
      let existingAttachments: UploadedFile[] = [];
      try {
        const attachmentsResponse = await apiClient.get<{ data: AttachmentResponse[] }>(
          `/requests/${requestId}/attachments`
        );
        existingAttachments = (attachmentsResponse.data.data || []).map((att) => ({
          id: att.id,
          name: att.original_filename,
          size: att.size,
          type: att.mime_type,
          progress: 100,
          status: 'completed' as const,
          attachmentId: att.id,
        }));
      } catch (err) {
        console.warn('Failed to load attachments:', err);
      }

      // Hide loading, show form
      loadingContainer.classList.add('hidden');
      formContainer.classList.remove('hidden');
      lastSavedContainer.classList.remove('hidden');

      // Update last saved timestamp
      updateLastSaved(currentRequest.updated_at);

      // Initialize the form with existing data
      formInstance = await createRequestForm({
        container: formContainer,
        isEditMode: true,
        initialData: {
          title: currentRequest.title || '',
          description: currentRequest.description || '',
          expenseType: currentRequest.category || '',
          amount: currentRequest.amount?.toString() || '',
          currency: currentRequest.currency || 'CHF',
          frequency: 'ONE_TIME', // Default, as not stored in DB
        },
        onSubmit: handleSubmit,
        onSaveDraft: handleSaveDraft,
      });

      // Initialize file upload component
      const fileUploadContainer = document.getElementById('file-upload-container');
      if (fileUploadContainer && requestId) {
        fileUploadInstance = createFileUpload({
          container: fileUploadContainer,
          requestId,
          initialFiles: existingAttachments,
          autoUpload: true,
          onChange: (files) => {
            console.log('Files changed:', files);
          },
          onUploadComplete: (file) => {
            console.log('File uploaded:', file);
          },
          onRemove: (file) => {
            console.log('File removed:', file);
          },
        });
      }

      // Setup auto-save (every 30 seconds if there are changes)
      setupAutoSave();
    } catch (error) {
      console.error('Failed to load request:', error);

      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

      loadingContainer.innerHTML = `
        <div class="flex justify-center mb-4">
          ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
        </div>
        <h2 class="text-xl font-bold text-foreground mb-2">Failed to Load Request</h2>
        <p class="text-muted-foreground mb-4">${errorMessage}</p>
        <button onclick="location.reload()" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
          ${renderIcon('refresh', { size: 'sm' }).outerHTML}
          <span>Retry</span>
        </button>
      `;
    }
  }, 0);

  /**
   * Update last saved timestamp display
   */
  function updateLastSaved(timestamp: string): void {
    const lastSavedText = document.getElementById('last-saved-text');
    if (lastSavedText) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      let timeText: string;
      if (diffMins < 1) {
        timeText = 'Just now';
      } else if (diffMins < 60) {
        timeText = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else {
        timeText = date.toLocaleString('en-CH', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      lastSavedText.textContent = `Last saved: ${timeText}`;
    }
  }

  /**
   * Setup auto-save functionality
   */
  function setupAutoSave(): void {
    // Clear any existing timer
    if (autoSaveTimer) {
      clearInterval(autoSaveTimer);
    }

    // Auto-save every 30 seconds
    autoSaveTimer = setInterval(async () => {
      if (formInstance && currentRequest?.status === 'DRAFT') {
        const formData = formInstance.getFormData();
        // Only auto-save if there's at least a title
        if (formData.title.trim()) {
          try {
            await saveChanges(formData, true);
          } catch (err) {
            console.warn('Auto-save failed:', err);
          }
        }
      }
    }, 30000);
  }

  /**
   * Save changes to the draft
   */
  async function saveChanges(data: RequestFormData, isAutoSave: boolean = false): Promise<void> {
    if (!currentRequest) return;

    try {
      // Check for concurrent edit conflict
      const checkResponse = await apiClient.get<{ data: ExpenditureRequestResponse }>(
        `/requests/${requestId}`
      );

      if (checkResponse.data.data.version !== currentVersion) {
        // Conflict detected
        const shouldOverwrite = await confirm({
          title: 'Conflict Detected',
          message: 'This request has been modified by another user or session. Would you like to overwrite those changes with your current version?',
          confirmText: 'Overwrite',
          destructive: true,
          cancelText: 'Cancel & Reload',
          icon: 'alertTriangle',
        });

        if (!shouldOverwrite) {
          window.location.reload();
          return;
        }

        // Update our version to the server's version before saving
        currentVersion = checkResponse.data.data.version;
      }

      // Save the changes
      const updateResponse = await apiClient.put<{ data: ExpenditureRequestResponse; message: string }>(
        `/requests/${requestId}`,
        {
          title: data.title,
          description: data.description || '',
          amount: data.amount ? parseFloat(data.amount) : null,
          currency: data.currency || 'CHF',
          category: data.expenseType || null,
        }
      );

      // Update version and timestamp
      currentVersion = updateResponse.data.data.version;
      currentRequest = updateResponse.data.data;
      updateLastSaved(updateResponse.data.data.updated_at);

      if (!isAutoSave) {
        await alert({
          title: 'Draft Saved',
          message: 'Your changes have been saved successfully.',
          buttonText: 'OK',
          type: 'success',
        });
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      if (!isAutoSave) {
        throw error;
      }
    }
  }

  /**
   * Handle form submission (submit for approval)
   */
  async function handleSubmit(data: RequestFormData): Promise<void> {
    if (!currentRequest) return;

    try {
      // First save any pending changes
      await saveChanges(data, true);

      // Then submit for approval
      await apiClient.post<{ data: unknown; message: string }>(
        `/requests/${requestId}/submit`
      );

      await alert({
        title: 'Request Submitted',
        message: `Your expenditure request has been submitted for approval.\n\nRequest ID: ${requestId}\n\nYou will receive notifications as your request progresses through the approval workflow.`,
        buttonText: 'OK',
        type: 'success',
      });

      // Navigate to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to submit request:', error);

      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

      await alert({
        title: 'Submission Failed',
        message: `Failed to submit your request: ${errorMessage}\n\nPlease try again or save as draft.`,
        buttonText: 'OK',
        type: 'error',
      });

      throw error;
    }
  }

  /**
   * Handle save as draft
   */
  async function handleSaveDraft(data: RequestFormData): Promise<void> {
    try {
      await saveChanges(data, false);
    } catch (error) {
      console.error('Failed to save draft:', error);

      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

      await alert({
        title: 'Save Failed',
        message: `Failed to save your draft: ${errorMessage}\n\nPlease try again.`,
        buttonText: 'OK',
        type: 'error',
      });

      throw error;
    }
  }

  // Cleanup on navigation
  (container as HTMLElement & { cleanup?: () => void }).cleanup = () => {
    if (autoSaveTimer) {
      clearInterval(autoSaveTimer);
    }
    if (fileUploadInstance) {
      fileUploadInstance.destroy();
    }
  };

  return container;
}

/**
 * Factory function for the router
 */
export function createEditRequestView(context: RouteContext): HTMLElement {
  return editRequestView(context);
}

export default editRequestView;
